import { BillingCancellationMode } from "@redotech/crdb-enums/billing/billing-cancellation-mode";
import {
  type BillingCancellation,
  billingCancellationModeSchema,
  type BillingCancellationPolicy,
  billingCancellationPolicySchema,
  billingCancellationSchema,
} from "@redotech/redo-billing-common/billing-cancellation";
import { ServiceInvoiceType } from "@redotech/redo-billing-common/service-invoice";
import { unaryRpc } from "@redotech/rpc/definition";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const productTypeSchema = createEnum(ServiceInvoiceType, {
  enumName: "ServiceInvoiceType",
});

export interface GetProductBillingCancellationOutput {
  /** Null when the product isn't billing. */
  state: {
    policy: BillingCancellationPolicy;
    period: { start: Date; end: Date; nextEnd: Date } | null;
    cancellation: BillingCancellation | null;
  } | null;
}

const productBillingCancellationInputSchema = z.object({
  teamId: zExt.objectId(),
  productType: productTypeSchema,
});

export const getProductBillingCancellation = unaryRpc<
  "getProductBillingCancellation",
  { teamId: string; productType: ServiceInvoiceType },
  GetProductBillingCancellationOutput
>({
  name: "getProductBillingCancellation",
  input: productBillingCancellationInputSchema,
  output: z.object({
    state: z
      .object({
        policy: billingCancellationPolicySchema,
        period: z
          .object({
            start: z.coerce.date(),
            end: z.coerce.date(),
            nextEnd: z.coerce.date(),
          })
          .nullable(),
        cancellation: billingCancellationSchema.nullable(),
      })
      .nullable(),
  }),
});

export const cancelProductBilling = unaryRpc<
  "cancelProductBilling",
  {
    teamId: string;
    productType: ServiceInvoiceType;
    mode: BillingCancellationMode;
    customDate: Date | null;
  },
  { cancellation: BillingCancellation }
>({
  name: "cancelProductBilling",
  input: productBillingCancellationInputSchema.extend({
    mode: billingCancellationModeSchema,
    customDate: z.coerce.date().nullable(),
  }),
  output: z.object({ cancellation: billingCancellationSchema }),
});

export const revokeProductBillingCancellation = unaryRpc<
  "revokeProductBillingCancellation",
  { teamId: string; productType: ServiceInvoiceType },
  // False when the product can't resume renewal itself; the admin must.
  { renewalResumed: boolean }
>({
  name: "revokeProductBillingCancellation",
  input: productBillingCancellationInputSchema,
  output: z.object({ renewalResumed: z.boolean() }),
});
